import type { TextfieldProps } from '@lichtblick/photon/molecules/textfield';
import { i18n } from '@/i18n';

interface FieldOptions {
  fallback?: any;
  message?: string;
  optional?: boolean;
  pattern?: string;
  'data-testid'?: string;
}

/**
 * Type definitions for form field transformers
 */
export type FieldTransformer<T> = (value: FormDataEntryValue | null) => T;

export type TransformConfig<T extends Record<string, any>> = {
  [K in keyof T]-?: T[K] extends string ? FieldTransformer<T[K]> | undefined : FieldTransformer<T[K]>;
};

/**
 * Extracts and transforms FormData values to match a specific type T.
 * Only includes keys that exist in type T and transforms values according to T's type definitions.
 *
 * @template T - The target type to extract and transform to
 * @param formData - The FormData to extract values from
 * @param transformConfig - Configuration for field-specific transformations (required to ensure type safety)
 * @returns Object with only keys from T and properly typed values
 */
export const getInput = <T extends Record<string, any>>(formData: FormData, transformConfig: TransformConfig<T>): T => {
  const result = {} as T;

  // Only process keys that are explicitly defined in transformConfig
  // This ensures we only extract fields that are actually part of type T
  for (const [fieldKey, transformer] of Object.entries(transformConfig)) {
    const value = formData.get(fieldKey);
    const transformedValue = transformer ? transformer(value) : value;

    // Only include defined, scalar values
    if (transformedValue != null && !(transformedValue instanceof File)) {
      result[fieldKey as keyof T] = transformedValue as T[keyof T];
    }
  }

  return result;
};

/**
 * Common field transformers for typical form data types
 */
export const fieldTransformers: {
  string: FieldTransformer<string | undefined>;
  requiredString: FieldTransformer<string>;
  number: FieldTransformer<number | undefined>;
  requiredNumber: FieldTransformer<number>;
  boolean: FieldTransformer<boolean>;
} = {
  /**
   * Transforms to string, trims whitespace, returns undefined for empty strings
   */
  string: (value) => {
    if (value === null || value instanceof File) {
      return undefined;
    }

    return value.toString().trim() || undefined;
  },

  /**
   * Transforms to required string, trims whitespace, returns empty string for null/undefined
   */
  requiredString: (value) => fieldTransformers.string(value) ?? '',

  /**
   * Transforms to optional number, returns undefined for invalid/empty values
   */
  number: (value) => {
    if (value == null || value instanceof File) {
      return undefined;
    }

    const stringValue = value.toString().trim();

    if (stringValue === '') {
      return undefined;
    }

    const number = Number(stringValue);

    return Number.isNaN(number) ? undefined : number;
  },

  /**
   * Transforms to required number, returns 0 for invalid/empty values
   */
  requiredNumber: (value): number => fieldTransformers.number(value) ?? 0,

  /**
   * Transforms to boolean from string values
   */
  boolean: (value) => {
    if (value == null || value instanceof File) {
      return false;
    }

    return ['true', 'on', '1'].includes(value.toString().toLowerCase());
  },
};

export const getFieldProps = <T extends Record<string, any>>(
  input: T | undefined,
  name: keyof T & string,
  label: string,
  { fallback, message, optional, pattern, 'data-testid': testId }: FieldOptions = {},
): Pick<
  TextfieldProps,
  'defaultValue' | 'name' | 'required' | 'errorMessage' | 'pattern' | 'label' | 'data-testid'
> => {
  const defaultValue = (input?.[name] ?? fallback)?.toString();
  const required = !optional;
  let errorMessage = message;

  if (pattern) {
    errorMessage ??= i18n.validation.invalid;
  }

  if (required) {
    errorMessage ??= i18n.validation.missing;
  }

  return { defaultValue, name, required, label, errorMessage, pattern, 'data-testid': testId ?? `text-${name}` };
};
